/**
 * Events V2 Service
 * Centralized data fetching for events-v2
 */

import { db } from '../database/firebase';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import moment from 'moment';
import { buildEventQuery, getManagerApiFilters } from '../utils/eventListUtils';
import { withEventSlugAsRaId } from '../utils/eventIdentity';
import { getPanelAccessToken } from './apiAuthProvider';

const useApiData = process.env.REACT_APP_USE_API_DATA === 'true';
const DEFAULT_PAGE_SIZE = 50;

const getApiUrl = () => {
  const base = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';
  return base.replace(/\/$/, '');
};

/**
 * NestJS API'den etkinlik listesi (REACT_APP_USE_API_DATA=true iken).
 * @returns {{ data: Array, total: number, page: number, perPage: number }}
 */
const getEventsV2FromApi = async (options = {}) => {
  const page = Math.max(1, Number(options.page) || 1);
  const perPage = Math.max(1, Number(options.perPage ?? options.limitCount) || DEFAULT_PAGE_SIZE);
  const params = new URLSearchParams({
    page: String(page),
    perPage: String(perPage),
    sort: options.sort || 'startdate',
    order: options.order || 'asc',
    includeRevenue: 'true',
  });
  if (options.status) {
    params.set('status', options.status);
  }

  const apiFilters = options.apiFilters ?? {};
  Object.entries(apiFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  const token = getPanelAccessToken();
  const response = await fetch(`${getApiUrl()}/events?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch events (${response.status})`);
  }

  const json = await response.json();
  return {
    data: (json.data ?? []).map(withEventSlugAsRaId),
    total: json.total ?? 0,
    page: json.page ?? page,
    perPage: json.perPage ?? perPage,
  };
};

/**
 * Fetch events based on user permissions
 * @returns {Promise<{ data: Array, total: number, page: number, perPage: number }>}
 */
export const getEventsV2 = async (permissions, options = {}) => {
  if (useApiData) {
    return getEventsV2FromApi({
      ...options,
      status: Object.prototype.hasOwnProperty.call(options, 'status')
        ? options.status
        : 'upcoming',
      apiFilters: {
        ...getManagerApiFilters(permissions),
        ...(options.apiFilters ?? {}),
      },
    });
  }

  try {
    const queryConfig = buildEventQuery(permissions, options);
    const collectionRef = collection(db, 'events-v2');

    let q = query(
      collectionRef,
      orderBy(queryConfig.orderBy, queryConfig.orderDirection),
      limit(queryConfig.limit),
    );

    if (queryConfig.where && queryConfig.where.length > 0) {
      queryConfig.where.forEach(([field, operator, value]) => {
        q = query(q, where(field, operator, value));
      });
    }

    const dataSnapshot = await getDocs(q);

    if (!dataSnapshot) {
      return { data: [], total: 0, page: 1, perPage: DEFAULT_PAGE_SIZE };
    }

    const events = [];
    dataSnapshot.forEach((doc) => {
      const eventData = doc.data();
      events.push({
        id: doc.id,
        ...eventData,
      });
    });

    return {
      data: events,
      total: events.length,
      page: 1,
      perPage: events.length || DEFAULT_PAGE_SIZE,
    };
  } catch (error) {
    console.error('Error fetching events-v2:', error);
    throw new Error(`Failed to fetch events: ${error.message}`);
  }
};

/**
 * Fetch events with additional filtering
 */
export const getFilteredEventsV2 = async (permissions, filters = {}) => {
  const {
    startDate = moment().subtract(7, 'hours').toDate(),
    endDate = null,
    eventType = null,
    city = null,
    category = null,
    limitCount = 100,
  } = filters;

  const options = {
    startDate,
    limitCount,
  };

  const result = await getEventsV2(permissions, options);
  let events = result.data ?? [];

  if (endDate) {
    events = events.filter((event) => {
      const eventEnd = event.enddate?.toDate ? event.enddate.toDate() : event.enddate;
      return moment(eventEnd).isBefore(moment(endDate));
    });
  }

  if (eventType) {
    events = events.filter((event) => event.type === eventType);
  }

  if (city) {
    events = events.filter((event) => event.city === city);
  }

  if (category) {
    events = events.filter((event) => event.category === category);
  }

  return events;
};

const eventsV2Service = {
  getEventsV2,
  getFilteredEventsV2,
};

export default eventsV2Service;
